import { forwardRef, useId, type PropsWithChildren } from "react";
import {
  CoreButtonGroup,
  type PropsWithOptionalTestID,
} from "@boi/components-core";

import * as S from "./ButtonGroup.styled";
import type { Item } from "./ButtonGroup.types";
import { ButtonGroupItem, type ButtonGroupItemProps } from "./ButtonGroupItem";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { Fieldset, type FieldsetProps } from "../Fieldset";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

export type ButtonGroupProps = PropsWithOptionalTestID &
  Pick<ButtonGroupItemProps, "typographyVariant"> &
  Pick<
    FieldsetProps,
    "legend" | "legendHint" | "legendTooltipProps" | "legendHintTooltipProps"
  > &
  PropsWithChildren<{
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
    variant?: CoreButtonGroup.Variant;
    name: string;
    items: Item[];
    onChange?: ButtonGroupItemProps["onChange"];
  }>;

const ButtonGroup = forwardRef<HTMLFieldSetElement, ButtonGroupProps>(
  (props, ref) => {
    const {
      alertProps,
      legend,
      legendHint,
      legendTooltipProps,
      legendHintTooltipProps,
      variant = CoreButtonGroup.DEFAULT_VARIANT,
      name,
      items,
      onChange,
      typographyVariant,
      testID = "button-group",
    } = props;

    const alertId = useId();
    const ariaAlertAssociationProps = buildAlertAssociationProps(
      alertId,
      props
    );

    return (
      <InputWrapper
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <Fieldset
          ref={ref}
          legend={legend}
          legendHint={legendHint}
          legendTooltipProps={legendTooltipProps}
          legendHintTooltipProps={legendHintTooltipProps}
          testID={testID}
          ariaAlertAssociationProps={ariaAlertAssociationProps}
        >
          <S.Container $variant={variant}>
            {items.map((item) => (
              <ButtonGroupItem
                key={item.id}
                label={item.label}
                hasError={item.hasError}
                fullWidth={["stretch", "stacked"].includes(variant)}
                textAlign={variant === "stacked" ? "left" : "center"}
                typographyVariant={typographyVariant}
                name={name}
                value={item.value}
                checked={item.checked}
                onChange={onChange}
                testID={`${testID}-item-${item.id}`}
              />
            ))}
          </S.Container>
        </Fieldset>
      </InputWrapper>
    );
  }
);

ButtonGroup.displayName = "ButtonGroup";

export default ButtonGroup;
